import { useRef } from "react";
import styled from "styled-components";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useTranslation } from "react-i18next";

// ─── Data Static Overrides ───────────────────────────────────────────
// Visual data remains here, text moves to i18n
const SERVICE_CONFIG = [
  {
    id: 0,
    number: "01",
    bg: "#1E0F0A",
    pill: "#B87355",
    img: "./assets/card-1.jpg",
  },
  {
    id: 1,
    number: "02",
    bg: "#F7F3EE",
    pill: "#C9AA8C",
    img: "./assets/card-2.jpg",
  },
  {
    id: 2,
    number: "03",
    bg: "#2E1F14",
    pill: "#C49A6C",
    img: "./assets/card-3.jpg",
  },
  {
    id: 3,
    number: "04",
    bg: "#EDE7DC",
    pill: "#7A5C47",
    img: "./assets/card-4.jpg",
  },
  {
    id: 4,
    number: "05",
    bg: "#2E1F14",
    pill: "#C49A6C",
    img: "./assets/card-4.jpg",
  },
];

const TOTAL = SERVICE_CONFIG.length;
const isLight = (bg) => ["#F7F3EE", "#EDE7DC"].includes(bg);

// ─── Styled Components ───────────────────────────────────────────────
// ... (All styled components remain exactly as in your provided code) ...
const Section = styled.section`
  position: relative;
  width: 100%;
  background: var(--ink);
`;
const ScrollTrack = styled.div`
  height: ${TOTAL * 100 + 50}vh;
  position: relative;
`;
const Sticky = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;
const LabelBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  padding: 2.2rem 5vw;
  display: flex;
  align-items: center;
  justify-content: space-between;
  mix-blend-mode: difference;
  pointer-events: none;
`;
const LabelLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;
const LabelDash = styled.div`
  width: 32px;
  height: 1px;
  background: var(--cream);
  opacity: 0.6;
`;
const LabelText = styled.span`
  font-family: var(--font-sans);
  font-size: 0.62rem;
  font-weight: 300;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--cream);
  opacity: 0.7;
`;
const LabelCount = styled.span`
  font-family: var(--font-serif);
  font-size: 0.9rem;
  font-weight: 300;
  font-style: italic;
  color: var(--cream);
  opacity: 0.5;
`;
const Rail = styled(motion.div)`
  display: flex;
  flex-direction: row;
  height: 100%;
  will-change: transform;
`;
const CardWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: ${({ $bg }) => $bg};
  display: grid;
  grid-template-columns: 1fr 1fr;
  @media (max-width: 750px) {
    grid-template-columns: 1fr;
    /* grid-template-rows: 1fr 1fr; */
  }
`;
const PhotoSide = styled.div`
  position: relative;
  overflow: hidden;
`;
const ParallaxImg = styled(motion.div)`
  position: absolute;
  inset: -15% 0;
  background-image: url(${({ $img }) => $img});
  background-size: cover;
  background-position: center;
  will-change: transform;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      100deg,
      ${({ $bg }) => $bg}CC 0%,
      transparent 50%
    );
  }
`;
const ContentSide = styled.div`
  position: relative;
  padding: clamp(3rem, 6vw, 6rem) clamp(2.5rem, 5vw, 5rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
`;
const GhostNumber = styled(motion.span)`
  position: absolute;
  bottom: -2rem;
  right: -1rem;
  font-family: var(--font-serif);
  font-size: clamp(14rem, 22vw, 26rem);
  font-weight: 300;
  line-height: 0.8;
  color: transparent;
  -webkit-text-stroke: 1px
    ${({ $light }) =>
      $light ? "rgba(46,31,20,0.06)" : "rgba(247,243,238,0.05)"};
  pointer-events: none;
  user-select: none;
  letter-spacing: -0.04em;
`;
const ServiceNumber = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.62rem;
  font-weight: 300;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: ${({ $light }) =>
    $light ? "var(--mocha)" : "rgba(247,243,238,0.45)"};
  margin-bottom: 2.5rem;
  display: block;
`;
const TitleWrap = styled.div`
  overflow: visible;
  margin-bottom: 0.1rem;
`;
const Title = styled(motion.h3)`
  font-family: var(--font-serif);
  font-size: clamp(3rem, 5vw, 5.5rem);
  font-weight: 300;
  line-height: 0.9;
  color: ${({ $light }) => ($light ? "var(--ink)" : "var(--cream)")};
  letter-spacing: -0.02em;
`;
const TitleItalic = styled(Title)`
  font-style: italic;
  color: ${({ $light }) => ($light ? "var(--terracotta)" : "var(--warm-tan)")};
`;
const Divider = styled(motion.div)`
  width: 40px;
  height: 1px;
  background: ${({ $light }) =>
    $light ? "var(--warm-tan)" : "rgba(247,243,238,0.3)"};
  margin: 2rem 0;
`;
const Desc = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: clamp(0.78rem, 1vw, 0.88rem);
  font-weight: 200;
  letter-spacing: 0.05em;
  line-height: 1.9;
  color: ${({ $light }) =>
    $light ? "var(--ink-soft)" : "rgba(247,243,238,0.6)"};
  max-width: 36ch;
  margin-bottom: 2.5rem;
`;
const TagRow = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 3rem;
`;
const Tag = styled.span`
  font-family: var(--font-sans);
  font-size: 0.67rem;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 0.4rem 0.9rem;
  border-radius: 100px;
  background: ${({ $pill }) => $pill}22;
  color: ${({ $pill }) => $pill};
  border: 1px solid ${({ $pill }) => $pill}55;
`;
const BookLink = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.7rem;
  font-weight: 300;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ $pill }) => $pill};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  svg {
    transition: transform 0.3s;
  }
  &:hover svg {
    transform: translateX(6px);
  }
`;
const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: rgba(255, 255, 255, 0.08);
  z-index: 20;
`;
const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, var(--terracotta), var(--gold));
  transform-origin: left;
`;
const ScrollHint = styled(motion.div)`
  position: absolute;
  bottom: 2.5rem;
  right: 5vw;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-family: var(--font-sans);
  font-size: 0.58rem;
  font-weight: 300;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(247, 243, 238, 0.3);
`;
const ScrollLine = styled(motion.div)`
  width: 32px;
  height: 1px;
  background: rgba(247, 243, 238, 0.25);
  overflow: hidden;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--terracotta);
    animation: scanline 1.8s ease-in-out infinite;
  }
  @keyframes scanline {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

// ─── Per-card component ──────────────────────────────────────────────
function ServiceCard({ service, index, scrollYProgress }) {
  const { t } = useTranslation();
  const light = isLight(service.bg);
  const bandStart = index / TOTAL;
  const bandEnd = (index + 1) / TOTAL;

  const rawImgY = useTransform(
    scrollYProgress,
    [bandStart, bandEnd],
    ["-8%", "8%"],
  );
  const imgY = useSpring(rawImgY, { stiffness: 80, damping: 20 });

  const rawContentX = useTransform(
    scrollYProgress,
    [bandStart - 0.5 / TOTAL, bandStart + 0.5 / TOTAL],
    ["6%", "0%"],
  );
  const contentX = useSpring(rawContentX, { stiffness: 100, damping: 22 });
  const rawOpacity = useTransform(
    scrollYProgress,
    [bandStart - 0.3 / TOTAL, bandStart + 0.15 / TOTAL],
    [0, 1],
  );

  // Helper to split titles: "Bridal Experience" -> ["Bridal", "Experience"]
  const titleParts = t(`services.items.${index}.title`).split(" ");

  return (
    <CardWrap $bg={service.bg}>
      <PhotoSide>
        <ParallaxImg $img={service.img} $bg={service.bg} style={{ y: imgY }} />
      </PhotoSide>

      <ContentSide>
        <GhostNumber $light={light}>{service.number}</GhostNumber>

        <motion.div style={{ x: contentX, opacity: rawOpacity }}>
          <ServiceNumber $light={light}>
            {service.number} — {t(`services.items.${index}.sub`)}
          </ServiceNumber>

          <TitleWrap>
            <Title $light={light}>{titleParts[0]}</Title>
          </TitleWrap>
          {titleParts.length > 1 && (
            <TitleWrap>
              <TitleItalic as="h3" $light={light}>
                {titleParts.slice(1).join(" ")}
              </TitleItalic>
            </TitleWrap>
          )}

          <Divider
            $light={light}
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: false, margin: "-20%" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            style={{ transformOrigin: "left" }}
          />

          <Desc $light={light}>{t(`services.items.${index}.desc`)}</Desc>

          <TagRow>
            {t(`services.items.${index}.tags`, { returnObjects: true }).map(
              (tag) => (
                <Tag key={tag} $pill={service.pill}>
                  {tag}
                </Tag>
              ),
            )}
          </TagRow>

          <BookLink $pill={service.pill}>
            {t("services.book_now")}
            <svg width="20" height="10" viewBox="0 0 20 10" fill="none">
              <path
                d="M1 5h18M14 1l5 4-5 4"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </BookLink>
        </motion.div>
      </ContentSide>
    </CardWrap>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────
export default function ServicesSection() {
  const { t } = useTranslation();
  const trackRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start start", "end end"],
  });

  const rawX = useTransform(
    scrollYProgress,
    [0, 1],
    ["0vw", `-${(TOTAL - 1) * 100}vw`],
  );
  const x = useSpring(rawX, { stiffness: 60, damping: 18, mass: 0.8 });
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const hintOpacity = useTransform(scrollYProgress, [0, 0.12], [1, 0]);

  return (
    <Section>
      <ScrollTrack ref={trackRef}>
        <Sticky>
          <LabelBar>
            <LabelLeft>
              <LabelDash />
              <LabelText>{t("services.label")}</LabelText>
            </LabelLeft>
            <LabelCount>{t("services.scroll_explore")}</LabelCount>
          </LabelBar>

          <Rail style={{ x }}>
            {SERVICE_CONFIG.map((s, i) => (
              <ServiceCard
                key={s.id}
                service={s}
                index={i}
                scrollYProgress={scrollYProgress}
              />
            ))}
          </Rail>

          <ProgressBar>
            <ProgressFill style={{ scaleX }} />
          </ProgressBar>

          <ScrollHint style={{ opacity: hintOpacity }}>
            <ScrollLine />
            {t("services.scroll_down")}
          </ScrollHint>
        </Sticky>
      </ScrollTrack>
    </Section>
  );
}
